import { useEffect, useState } from "react";
import { safeGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  TShirt,
  Package,
  Users,
  Warehouse,
  WarningDiamond,
  Coins,
  Scissors,
  MapPin,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function KPI({ label, value, sub, icon: Icon, tone, testId }) {
  return (
    <div
      data-testid={testId}
      className="kpi-hover border p-6 md:p-7 flex flex-col justify-between h-full"
      style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
    >
      <div className="flex items-center justify-between">
        <div className="data-label">{label}</div>
        <Icon size={18} weight="light" className="text-[#8F8D84]" />
      </div>
      <div className="mt-8">
        <div className="font-serif text-4xl md:text-5xl leading-none tracking-tight font-light">
          {value}
        </div>
        {sub && (
          <div className="mt-3 text-xs text-[#8F8D84]" style={{ color: tone }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const { user, can } = useAuth();
  const { t, tEnum, localName, fmtCurrency, fmtNumber, lang } = useI18n();
  const [data, setData] = useState(null);
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    (async () => {
      const [cc, colls, locs] = await Promise.all([
        can("dashboard:view") ? safeGet("/dashboard/command-center") : Promise.resolve({}),
        can("collection:view") ? safeGet("/collections") : Promise.resolve({}),
        can("location:view") ? safeGet("/locations") : Promise.resolve({}),
      ]);
      if (cc.data) setData(cc.data);
      if (colls.data) setCollections(colls.data);
      if (locs.data) setLocations(locs.data);
    })();
  }, [can]);

  const collectionsById = Object.fromEntries(collections.map((c) => [c.id, c]));
  const locationsById = Object.fromEntries(locations.map((l) => [l.id, l]));
  const k = data?.kpis || {};
  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div data-testid="command-center-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline mb-3">{t("cc.eyebrow")}</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">
            {t("cc.greeting", { name: firstName })}
          </h1>
          <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{t("cc.subtitle")}</p>
        </div>
        <div className="hidden md:block text-right">
          <div className="data-label">{t("common.timezone")}</div>
          <div className="font-mono text-sm mt-2">Europe / Istanbul</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10" data-testid="kpi-grid">
        <KPI label={t("cc.kpi.styles")} value={k.styles ?? "—"} sub={t("cc.kpi.stylesSub", { n: k.collections ?? 0 })} icon={TShirt} testId="kpi-styles" />
        <KPI label={t("cc.kpi.materials")} value={k.materials ?? "—"} sub={t("cc.kpi.materialsSub", { n: k.suppliers ?? 0 })} icon={Package} testId="kpi-materials" />
        <KPI label={t("cc.kpi.fabricOnHand")} value={`${fmtNumber(k.fabric_meters_on_hand ?? 0)} m`} sub={t("cc.kpi.reservedSuffix", { reserved: k.fabric_meters_reserved ?? 0, rolls: k.fabric_rolls ?? 0 })} icon={Scissors} testId="kpi-fabric" />
        <KPI label={t("cc.kpi.inventoryValue")} value={fmtCurrency(k.inventory_value_eur)} sub={t("cc.kpi.inventoryValueSub")} icon={Coins} testId="kpi-inventory-value" />
        <KPI label={t("cc.kpi.lowStock")} value={k.low_stock_alerts ?? 0} sub={t("cc.kpi.lowStockSub")} icon={WarningDiamond} tone={k.low_stock_alerts ? "var(--nylsu-warning)" : undefined} testId="kpi-low-stock" />
        <KPI label={t("cc.kpi.locations")} value={k.locations ?? 0} sub={t("cc.kpi.locationsSub")} icon={MapPin} testId="kpi-locations" />
        <KPI label={t("cc.kpi.suppliers")} value={k.suppliers ?? 0} sub={t("cc.kpi.suppliersSub")} icon={Users} testId="kpi-suppliers" />
        <KPI label={t("cc.kpi.fabricRolls")} value={k.fabric_rolls ?? 0} sub={t("cc.kpi.rollsSub")} icon={Warehouse} testId="kpi-rolls" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div
          className="lg:col-span-2 border p-6 md:p-8"
          style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
          data-testid="low-stock-panel"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-2">{t("cc.lowStock.eyebrow")}</div>
              <h2 className="font-serif text-2xl tracking-tight">{t("cc.lowStock.title")}</h2>
            </div>
            <Link to="/materials" className="text-xs uppercase tracking-[0.2em] link-underline">
              {t("common.viewAll")}
            </Link>
          </div>

          {data?.low_stock?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-4 py-3">{t("common.code")}</th>
                  <th className="data-label px-4 py-3">{t("common.name")}</th>
                  <th className="data-label px-4 py-3 text-right">{t("common.stock")}</th>
                  <th className="data-label px-4 py-3 text-right">{t("common.reorder")}</th>
                  <th className="data-label px-4 py-3 text-right">{t("cc.lowStock.shortage")}</th>
                </tr>
              </thead>
              <tbody>
                {data.low_stock.map((m) => (
                  <tr
                    key={m.code}
                    className="border-t h-14"
                    style={{ borderColor: "var(--nylsu-border)" }}
                    data-testid={`low-stock-row-${m.code}`}
                  >
                    <td className="px-4 font-mono text-xs">{m.code}</td>
                    <td className="px-4">{lang === "tr" && m.name_tr ? m.name_tr : m.name}</td>
                    <td className="px-4 text-right font-mono">{m.stock} {tEnum("material.unit", m.unit)}</td>
                    <td className="px-4 text-right font-mono text-[#8F8D84]">{m.reorder_point} {tEnum("material.unit", m.unit)}</td>
                    <td className="px-4 text-right font-mono" style={{ color: "var(--nylsu-warning)" }}>
                      −{m.shortage.toFixed(1)} {tEnum("material.unit", m.unit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-sm text-[#8F8D84] py-8 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
              {t("cc.lowStock.empty")}
            </div>
          )}
        </div>

        <div
          className="border p-6 md:p-8"
          style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
          data-testid="collections-panel"
        >
          <div className="overline mb-2">{t("cc.collections.eyebrow")}</div>
          <h2 className="font-serif text-2xl tracking-tight mb-6">{t("cc.collections.title")}</h2>
          <div className="space-y-5">
            {(data?.styles_per_collection || []).map((row) => {
              const coll = collectionsById[row._id];
              const total = data.styles_per_collection.reduce((a, b) => a + b.styles, 0) || 1;
              const pct = Math.round((row.styles / total) * 100);
              return (
                <div key={row._id} data-testid={`coll-row-${row._id}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-serif text-lg">{localName(coll) || "—"}</div>
                    <div className="font-mono text-xs text-[#8F8D84]">{row.styles} {t("cc.kpi.styles").toLowerCase()}</div>
                  </div>
                  <div className="h-[2px] w-full" style={{ background: "var(--nylsu-border)" }}>
                    <div className="h-full" style={{ width: `${pct}%`, background: "var(--nylsu-gold)" }} />
                  </div>
                  <div className="mt-1 overline">
                    {coll ? `${tEnum("season", coll.season)} ${coll.year} · ${tEnum("collection.status", coll.status)}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="lg:col-span-3 border p-6 md:p-8"
          style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
          data-testid="locations-panel"
        >
          <div className="overline mb-2">{t("cc.locations.eyebrow")}</div>
          <h2 className="font-serif text-2xl tracking-tight mb-6">{t("cc.locations.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {locations.map((loc) => {
              const row = (data?.inventory_by_location || []).find((r) => r._id === loc.id);
              return (
                <div
                  key={loc.id}
                  className="border p-5"
                  style={{ borderColor: "var(--nylsu-border)" }}
                  data-testid={`loc-card-${loc.code}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="overline">{tEnum("location.type", loc.type)}</div>
                    <span className="font-mono text-[10px] text-[#8F8D84]">{loc.code}</span>
                  </div>
                  <div className="font-serif text-2xl mt-3">{loc.name}</div>
                  <div className="text-xs text-[#8F8D84] mt-1">{loc.city}, {loc.country}</div>
                  <div className="mt-5 flex items-end justify-between">
                    <div>
                      <div className="data-label">{t("cc.locations.unitsOnHand")}</div>
                      <div className="font-mono text-xl mt-1">{Math.round(row?.on_hand_units || 0)}</div>
                    </div>
                    <div className="text-right">
                      <div className="data-label">{t("cc.locations.movements")}</div>
                      <div className="font-mono text-xl mt-1">{row?.movements || 0}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
